/**
 * Routes.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import type { Route } from '../catalog/types'

export type CustomerRouteMode = 'login' | 'signup' | 'activate' | 'forgot-password' | 'reset-password' | 'account'

export const CUSTOMER_ACCOUNT_BASE_SLUG = 'dashboard'
export const CUSTOMER_LOGIN_SLUG = 'dashboard/login'
export const CUSTOMER_SIGNUP_SLUG = 'dashboard/signup'
export const CUSTOMER_ACTIVATE_SLUG = 'account/activate'
export const CUSTOMER_FORGOT_PASSWORD_SLUG = 'dashboard/forgot-password'
export const CUSTOMER_RESET_PASSWORD_SLUG = 'dashboard/reset-password'
export const CUSTOMER_REGISTER_SLUG = 'dashboard/register'

const CUSTOMER_ACCOUNT_BASE_SLUGS = ['dashboard', 'account']
const CUSTOMER_ROUTE_SUFFIXES: Record<Exclude<CustomerRouteMode, 'account'>, string[]> = {
  login: ['login'],
  signup: ['signup', 'register'],
  activate: ['activate'],
  'forgot-password': ['forgot-password'],
  'reset-password': ['reset-password'],
}

export const normalizePageSlug = (slug: string) =>
  slug
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()

/**
 * Resolve customer route mode.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const resolveCustomerRouteMode = (route: Route): CustomerRouteMode | null => {
  if (route.mode !== 'page') return null
  const slug = normalizePageSlug(route.slug)
  for (const [mode, suffixes] of Object.entries(CUSTOMER_ROUTE_SUFFIXES)) {
    for (const suffix of suffixes) {
      if (slug === suffix || CUSTOMER_ACCOUNT_BASE_SLUGS.some((baseSlug) => slug === `${baseSlug}/${suffix}`)) {
        return mode as Exclude<CustomerRouteMode, 'account'>
      }
    }
  }
  if (CUSTOMER_ACCOUNT_BASE_SLUGS.some((baseSlug) => slug === baseSlug || slug.startsWith(baseSlug + '/'))) {
    return 'account'
  }
  return null
}

/**
 * Is customer route.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const isCustomerRoute = (route: Route) => resolveCustomerRouteMode(route) !== null
